/** Canonical facility layout constants — single source of truth for both SVG and Phaser renderers. */

// ── Room definitions ──────────────────────────────────────────────────────────
// stateId maps to state.rooms[].id in the game reducer
export const FACILITY_ROOMS = [
  // VEG (2×2) — VEG-01 unchanged; VEG-02 shifts right 2 (col 5→7)
  { id: 'room-veg-01',     col: 0,  row: 0, w: 2, h: 2, type: 'veg',        stateId: 'room-02',  locked: false },
  { id: 'room-veg-02',     col: 7,  row: 0, w: 2, h: 2, type: 'veg',        stateId: 'room-veg-02',  locked: true, unlockCost: 8000,  unlockLabel: 'Veg Room 02' },

  // GREENHOUSE — shifts right 1 (col 2→3) to open col-2 gap for C1
  { id: 'room-greenhouse', col: 3,  row: 0, w: 3, h: 3, type: 'greenhouse', stateId: 'room-01',  locked: false },

  // FLOWER rooms — all shift down 1 (row 3→4) to open row-3 gap for C4 spine
  // flower-01: col unchanged (0); flower-02: col 2→3; flower-03: col 4→6; flower-04: col 6→9
  // flower-01 and flower-02 are both tiles of the same Indoor Flower Room (room-03); this is intentional,
  // matching the lab-main + lab-ext pattern where multiple visual tiles represent one state room.
  { id: 'room-flower-01',  col: 0,  row: 4, w: 2, h: 4, type: 'flower',     stateId: 'room-03',  locked: false },
  { id: 'room-flower-02',  col: 3,  row: 4, w: 2, h: 4, type: 'flower',     stateId: 'room-03',  locked: false },
  { id: 'room-flower-03',  col: 6,  row: 4, w: 2, h: 4, type: 'flower',     stateId: 'room-04',  locked: false },
  { id: 'room-flower-04',  col: 9,  row: 4, w: 2, h: 4, type: 'flower',     stateId: 'room-flower-04',  locked: true, unlockCost: 20000, unlockLabel: 'Flower Room 04' },

  // LAB — shifts right 5 (col 7→12); ext tile follows
  { id: 'room-lab-main',   col: 12, row: 0, w: 2, h: 2, type: 'lab',        stateId: 'room-lab', locked: false },
  { id: 'room-lab-ext',    col: 12, row: 2, w: 1, h: 2, type: 'lab',        stateId: 'room-lab', locked: false, noClick: true },

  // CURING / PROCESSING — shift down 2 (row 7→9) to open row-8 gap for C5
  { id: 'room-curing',     col: 0,  row: 9, w: 3, h: 2, type: 'curing',     stateId: 'room-curing',     locked: true, unlockCost: 15000, unlockLabel: 'Curing Room' },
  { id: 'room-processing', col: 4,  row: 9, w: 4, h: 2, type: 'processing', stateId: 'room-processing', locked: true, unlockCost: 25000, unlockLabel: 'Processing — Stage 2', minStage: 2 },

  // MARKETING OFFICE — "Front Office & Brand Studio" between production and lab
  { id: 'room-marketing',  col: 10, row: 0, w: 2, h: 2, type: 'marketing',  stateId: null, locked: true, unlockLabel: 'Marketing Office', techGate: 'D4' },

  // FACILITY 2 GHOST — shifts right 5 (col 10→15)
  { id: 'facility-2-ghost',col: 15, row: 2, w: 4, h: 4, type: 'future',     locked: true, ghost: true, unlockLabel: 'Facility 2 — Stage 3' },
];

export const CORRIDORS = [
  { id: 'c1', col: 2,  row: 0, w: 1, h: 3 },   // VEG-01 east ↔ Greenhouse west
  { id: 'c2', col: 6,  row: 0, w: 1, h: 3 },   // Greenhouse east ↔ VEG-02 west
  { id: 'c3', col: 5,  row: 3, w: 1, h: 1 },   // Greenhouse south ↔ C4 spine
  { id: 'c4', col: 0,  row: 3, w: 11, h: 1 },  // Horizontal spine above flower row
  { id: 'c5', col: 3,  row: 8, w: 2,  h: 1 },  // Below flower-02 → curing (cols 3–5 match flower-02 footprint)
  { id: 'c6', col: 9,  row: 0, w: 1,  h: 2 },  // VEG-02 east ↔ Marketing west
];

// Pre-compute slab bounds (non-ghost rooms + 1 unit margin) — shared by the 3D scene (construction sites / ghost footprints) and computeViewBox
export const SLAB_BOUNDS = (() => {
  const nonGhost = FACILITY_ROOMS.filter(r => !r.ghost);
  let minCol = Infinity, minRow = Infinity, maxCol = -Infinity, maxRow = -Infinity;
  nonGhost.forEach(({ col, row, w, h }) => {
    minCol = Math.min(minCol, col);
    minRow = Math.min(minRow, row);
    maxCol = Math.max(maxCol, col + w);
    maxRow = Math.max(maxRow, row + h);
  });
  return { col: minCol - 1, row: minRow - 1, w: (maxCol - minCol) + 2, h: (maxRow - minRow) + 2 };
})();

export const DOORS = [
  { wallRoomId: 'room-greenhouse', side: 'west',  type: 'top-edge',     corridorId: 'c1' },
  { wallRoomId: 'room-greenhouse', side: 'east',  type: 'wall-overlay', corridorId: 'c2' },
  { wallRoomId: 'room-greenhouse', side: 'south', type: 'wall-overlay', corridorId: 'c3' },
  { wallRoomId: 'room-flower-01',  side: 'north', type: 'top-edge',     corridorId: 'c4' },
  { wallRoomId: 'room-flower-02',  side: 'north', type: 'top-edge',     corridorId: 'c4' },
  { wallRoomId: 'room-flower-03',  side: 'north', type: 'top-edge',     corridorId: 'c4' },
  { wallRoomId: 'room-flower-04',  side: 'north', type: 'top-edge',     corridorId: 'c4' },
  { wallRoomId: 'room-flower-02',  side: 'south', type: 'wall-overlay', corridorId: 'c5' },
  { wallRoomId: 'room-marketing',  side: 'west',  type: 'top-edge',     corridorId: 'c6' },
];

// ── Color palette ─────────────────────────────────────────────────────────────
export const ROOM_COLORS = {
  greenhouse: { top: '#163a28', south: '#0e2b1d', east: '#0a2218', stroke: '#3fb950', labelColor: '#3fb950' },
  veg:        { top: '#0e2e30', south: '#092328', east: '#071d20', stroke: '#58a6ff', labelColor: '#58a6ff' },
  flower:     { top: '#1a1040', south: '#12092e', east: '#0e0825', stroke: '#b347ff', labelColor: '#b347ff' },
  lab:        { top: '#0d1f3c', south: '#091728', east: '#070f1e', stroke: '#58a6ff', labelColor: '#40a0ff' },
  curing:     { top: '#1a1208', south: '#120d05', east: '#0e0a04', stroke: '#d29922', labelColor: '#d29922' },
  processing: { top: '#1a1010', south: '#120909', east: '#0e0707', stroke: '#f85149', labelColor: '#f85149' },
  marketing:  { top: '#1a1508', south: '#141005', east: '#100d04', stroke: '#d97706', labelColor: '#d97706' },
  future:     { top: '#111520', south: '#0d1018', east: '#0a0d14', stroke: '#1a2030', labelColor: '#2a3040' },
};
export const LOCKED_COLORS = { top: '#111520', south: '#0d1018', east: '#0a0d14', stroke: '#21262d' };
export const CORRIDOR_COLORS = { top: '#0e1215', south: '#0b0e11', east: '#090c0e', stroke: '#2a3040' };

/** Schema version for serialized layout data — increment when stateId mappings change. */
export const FACILITY_LAYOUT_VERSION = 1;
